import { useCallback, useEffect, useRef, useState } from 'react';
import type { Exercise, PlanStarter } from '@heraion/shared';

import { hydrateFromPlanStarter } from './planStarterHydration';
import type { LoggerDispatch } from './state/loggerDraft';
import type { LoggerDraft, WeightUnit } from './state/loggerDraftTypes';

// The empty «today» draft's two exits and its «empty» signal, split out of
// useTodayDraft. «Build one anyway» swaps the host's starter into the open
// draft; «New workout» discards the (empty) draft, waits for the close to
// land, then opens the host's «+» menu.

type Args = {
  draft: LoggerDraft;
  /** A today draft nobody has started. */
  pending: boolean;
  catalog: Exercise[] | null;
  defaultUnit: WeightUnit;
  dispatch: LoggerDispatch;
  onDiscard: () => void | Promise<boolean>;
  /** The host builds the draft toward MAV and hands the starter back. */
  onBuildAnyway?: () => Promise<PlanStarter | null>;
  onOpenCreateHub?: () => void;
  /** True while the draft is pending with no entry: the host loads the
   *  coverage behind «Build one anyway» only then. */
  onEmptyChange?: (empty: boolean) => void;
};

export function useTodayEmpty({
  draft,
  pending,
  catalog,
  defaultUnit,
  dispatch,
  onDiscard,
  onBuildAnyway,
  onOpenCreateHub,
  onEmptyChange,
}: Args): {
  building: boolean;
  onBuildAnyway: (() => void) | undefined;
  onOpenCreateHub: (() => void) | undefined;
} {
  const [building, setBuilding] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const empty = draft.todayProposal !== undefined && pending && draft.entries.length === 0;
  useEffect(() => {
    onEmptyChange?.(empty);
    return () => onEmptyChange?.(false);
  }, [empty, onEmptyChange]);

  // The host's starter replaces the empty draft in place (entries and view), so
  // no second draft opens. The draft then reads as unfitted and takes the
  // profile's minutes and intensity.
  const buildAnyway = useCallback(async () => {
    if (!onBuildAnyway || !catalog || building) return;
    setBuilding(true);
    try {
      const starter = await onBuildAnyway();
      if (!starter?.todayProposal || !mounted.current) return;
      const entries = hydrateFromPlanStarter(starter, defaultUnit, catalog).entries;
      dispatch({
        type: 'hydrate',
        draft: { ...draft, entries, todayProposal: starter.todayProposal },
      });
    } catch {
      // The host could not build (offline, say): the card stays empty and the
      // button can be tapped again. No new error UI: the logger has no light
      // network-error surface to reuse here.
    } finally {
      if (mounted.current) setBuilding(false);
    }
  }, [onBuildAnyway, catalog, building, defaultUnit, draft, dispatch]);

  // An empty draft holds nothing: it goes without a confirm (the discard path
  // already skips it). The discard is AWAITED (LG-76: the persisted clear, then
  // onClose), then one macrotask passes so React commits the logger Modal's
  // close before the hub's Modal opens: two Modals presented in one tick do not
  // stack reliably on iOS, and on web the hub would open behind the logger.
  const newWorkout = useCallback(async () => {
    if (!onOpenCreateHub) return;
    // Explicit false: not closed (a save is in flight), so the menu must not
    // open over the logger. A host that returns void has nothing to wait for.
    if ((await onDiscard()) === false) return;
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    onOpenCreateHub();
  }, [onDiscard, onOpenCreateHub]);

  return {
    building,
    onBuildAnyway: onBuildAnyway ? () => void buildAnyway() : undefined,
    onOpenCreateHub: onOpenCreateHub ? () => void newWorkout() : undefined,
  };
}
